import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  QuickbooksInvoice,
  RemittanceRecord,
  CashReceipt,
  CashApplicationSettings,
  CashApplicationSettingsSchema,
  AppliedInvoice,
  BankTransactionRecord,
  CashAppEmailIngestionStatus,
} from '@dill/dill-shared';
import type { BankFeedStatus } from '../services/cashApplicationService';
import {
  fetchRemittanceRecordsExtraReducer,
  fetchCashReceiptsExtraReducer,
  fetchOpenInvoicesExtraReducer,
  processUploadedFileExtraReducer,
  applyPaymentExtraReducer,
  postUnappliedExtraReducer,
  approveOneClickExtraReducer,
  approveDepositGroupExtraReducer,
  deleteRemittanceRecordExtraReducer,
  hydrateSettingsFromSupplierExtraReducer,
  updateCashAppSettingsExtraReducer,
  bankFeedExtraReducers,
  bankTransactionExtraReducers,
  fetchEmailIngestionExtraReducer,
  enableEmailIngestionExtraReducer,
  disableEmailIngestionExtraReducer,
  fetchRemittanceEmailsNowExtraReducer,
} from '../reducers/cashApplicationReducers';

export interface UploadedFile {
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  status: 'processing' | 'matched' | 'review' | 'error';
}

export interface CashApplicationState {
  oneClickReviewItems: RemittanceRecord[];
  exceptionItems: RemittanceRecord[];
  /**
   * Advice we hold that the bank has not yet confirmed. Overlaps the two queues above by
   * design — settlement and allocation are independent questions, so the same record can be
   * waiting on money and waiting on a human at the same time.
   */
  awaitingSettlementItems: RemittanceRecord[];
  /** Money we hold that has no advice explaining it. The mirror image of the above. */
  bankTransactions: BankTransactionRecord[];
  postHistory: CashReceipt[];
  /**
   * Every remittance record the supplier has, by id — including the posted ones the queues drop.
   *
   * Post History needs it. A receipt lists the records it covers and holds no per-payment detail of
   * its own, so payer, amount, allocations and confidence are all read from here. Kept as a map
   * rather than an array because every read is by id.
   */
  remittanceRecordsById: Record<string, RemittanceRecord>;
  openInvoices: QuickbooksInvoice[];
  pendingAllocations: Record<string, AppliedInvoice[]>;
  settings: CashApplicationSettings;
  emailIngestion: CashAppEmailIngestionStatus | null;
  bankFeed: BankFeedStatus | null;
  // Single-use Plaid Link token, held only for the duration of the Link flow.
  bankFeedLinkToken: string | null;
  manualUploadFiles: UploadedFile[];
  loadingList: string[];
  error: string | null;
}

const initialState: CashApplicationState = {
  oneClickReviewItems: [],
  exceptionItems: [],
  awaitingSettlementItems: [],
  bankTransactions: [],
  postHistory: [],
  remittanceRecordsById: {},
  openInvoices: [],
  // Real settings arrive with the supplier document; defaults hold until then.
  settings: CashApplicationSettingsSchema.parse({}),
  pendingAllocations: {},
  emailIngestion: null,
  bankFeed: null,
  bankFeedLinkToken: null,
  manualUploadFiles: [],
  loadingList: [],
  error: null,
};

export const cashApplicationSlice = createSlice({
  name: 'cashApplication',
  initialState,
  reducers: {
    savePendingAllocations(
      state,
      action: PayloadAction<{ id: string; allocations: AppliedInvoice[] }>
    ) {
      state.pendingAllocations[action.payload.id] = action.payload.allocations;
    },

    // ── Post History ──────────────────────────────────────────────
    /**
     * Show the row as in-flight the moment Retry is pressed.
     *
     * Optimistic only as far as "we have asked". The outcome is the ERP's and arrives from the
     * poll — there is deliberately no companion reducer that declares success, because the previous
     * one did exactly that on a 2.5s timer and reported posts as applied that had never left Dill.
     */
    retryFailedPost(state, action: PayloadAction<string>) {
      const item = state.postHistory.find((r) => r.id === action.payload);
      // PARTIAL too, and it is the case the retry mostly exists for now — a guard on FAILED alone
      // would silently no-op and leave the button showing for a retry already under way.
      if (!item || (item.postStatus !== 'FAILED' && item.postStatus !== 'PARTIAL')) return;
      item.postStatus = 'POSTING';
      item.postError = null;
    },

    // ── Data Integrations ─────────────────────────────────────────
    addManualUploadFiles(state, action: PayloadAction<UploadedFile[]>) {
      state.manualUploadFiles = [...action.payload, ...state.manualUploadFiles];
    },

    removeManualUploadFile(state, action: PayloadAction<string>) {
      state.manualUploadFiles = state.manualUploadFiles.filter((f) => f.name !== action.payload);
    },

    updateManualUploadFileStatus(
      state,
      action: PayloadAction<{ name: string; status: UploadedFile['status'] }>
    ) {
      const file = state.manualUploadFiles.find((f) => f.name === action.payload.name);
      if (file) {
        file.status = action.payload.status;
      }
    },
  },
  extraReducers(builder) {
    fetchRemittanceRecordsExtraReducer(builder);
    fetchCashReceiptsExtraReducer(builder);
    fetchOpenInvoicesExtraReducer(builder);
    processUploadedFileExtraReducer(builder);
    applyPaymentExtraReducer(builder);
    postUnappliedExtraReducer(builder);
    approveOneClickExtraReducer(builder);
    approveDepositGroupExtraReducer(builder);
    deleteRemittanceRecordExtraReducer(builder);
    updateCashAppSettingsExtraReducer(builder);
    bankFeedExtraReducers(builder);
    bankTransactionExtraReducers(builder);
    fetchEmailIngestionExtraReducer(builder);
    enableEmailIngestionExtraReducer(builder);
    disableEmailIngestionExtraReducer(builder);
    fetchRemittanceEmailsNowExtraReducer(builder);
    // Must come last: RTK requires every addCase to be registered before any addMatcher.
    hydrateSettingsFromSupplierExtraReducer(builder);
  },
});

export const {
  savePendingAllocations,
  retryFailedPost,
  addManualUploadFiles,
  removeManualUploadFile,
  updateManualUploadFileStatus,
} = cashApplicationSlice.actions;

export default cashApplicationSlice.reducer;
